import * as React from 'react';

import {
  Autocomplete,
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import AgeRangesEditor from 'components/ReportRequestV2/AgeRangesEditor';
import ChipSelect from 'components/ReportRequestV2/ChipSelect';
import FormSection from 'components/ReportRequestV2/FormSection';
import GenderSplitSlider from 'components/ReportRequestV2/GenderSplitSlider';
import SwitchField from 'components/ReportRequestV2/SwitchField';
import useAvailableCountries from 'hooks/useAvailableCountries';
import useRegionsList from 'hooks/useRegionsList';

import { rowErrors, StepProps } from './types';
import {
  AGE_BOUNDS,
  ETHNICITIES,
  MAX_AGE_RANGES,
  SMOKING_HABITS,
  USA,
} from '../model/constants';
import { setField } from '../model/reducer';
import { AgeCategory, SmokingHabits } from '../model/types';

interface Props extends StepProps {
  producerId: number;
}

const DemographicsStep: React.FC<Props> = ({ state, dispatch, errors, producerId }) => {
  const { availableCountries, loading } = useAvailableCountries(producerId);
  const regions = useRegionsList();

  const countries = React.useMemo(
    () => Array.from(availableCountries).filter((c) => !!c.dbname),
    [availableCountries],
  );
  const selectedCountry = countries.find((c) => c.dbname === state.country) ?? null;
  const cltOptions = regions
    .filter((r) => r.country === state.country)
    .map((r) => ({ value: r.value, label: r.value }));
  const isUsa = state.country === USA;

  return (
    <>
      <FormSection title="Country" description="Where the target panel lives.">
        <Box sx={{ display: 'grid', gap: 3, maxWidth: 560 }}>
          <Autocomplete
            options={countries}
            loading={loading}
            value={selectedCountry}
            getOptionLabel={(c) => `${c.emoji} ${c.name}`}
            isOptionEqualToValue={(a, b) => a.dbname === b.dbname}
            onChange={(_, c) => dispatch({ type: 'setCountry', country: c?.dbname ?? null })}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Country"
                required
                error={!!errors.country}
                helperText={errors.country}
              />
            )}
          />
          {state.country && (
            <ChipSelect
              label="Regions (CLTs)"
              options={cltOptions}
              selected={state.clts}
              onToggle={(value) =>
                dispatch(
                  setField(
                    'clts',
                    state.clts.includes(value)
                      ? state.clts.filter((c) => c !== value)
                      : [...state.clts, value],
                  ),
                )
              }
              onClear={() => dispatch(setField('clts', []))}
              helperText="Optional. Limits the demographic translation model to reviews from these regions. Leave empty unless the panel targets a region."
              emptyText="No regions are available for this country."
            />
          )}
        </Box>
      </FormSection>

      <FormSection title="Panel" description="Who the report should represent.">
        <Box sx={{ display: 'grid', gap: 3 }}>
          <Box sx={{ maxWidth: 560 }}>
            <GenderSplitSlider
              female={state.includeFemale}
              onChange={(female) => dispatch(setField('includeFemale', female))}
              error={errors.includeFemale}
            />
          </Box>

          <FormControl>
            <FormLabel id="age-category-label">Age group</FormLabel>
            <ToggleButtonGroup
              exclusive
              size="small"
              color="primary"
              aria-labelledby="age-category-label"
              value={state.ageCategory}
              onChange={(_, category: AgeCategory | null) =>
                category && dispatch({ type: 'setAgeCategory', category })
              }
              sx={{ mt: 1 }}
            >
              <ToggleButton value="Adult">Adults</ToggleButton>
              <ToggleButton value="Children">Children</ToggleButton>
            </ToggleButtonGroup>
            <FormHelperText>
              Ages {AGE_BOUNDS[state.ageCategory].min}–{AGE_BOUNDS[state.ageCategory].max}. Switching resets the age ranges.
            </FormHelperText>
          </FormControl>

          <AgeRangesEditor
            ranges={state.ageRanges}
            onChange={(ranges) => dispatch(setField('ageRanges', ranges))}
            bounds={AGE_BOUNDS[state.ageCategory]}
            maxRanges={MAX_AGE_RANGES}
            rowErrors={rowErrors(errors, 'ageRanges')}
            error={errors.ageRanges ?? errors.ageRangesTotal}
          />

          <FormControl>
            <FormLabel id="smoking-label">Smoking habits</FormLabel>
            <ToggleButtonGroup
              exclusive
              size="small"
              color="primary"
              aria-labelledby="smoking-label"
              value={state.smokingHabits}
              onChange={(_, value: SmokingHabits | null) =>
                value && dispatch(setField('smokingHabits', value))
              }
              sx={{ mt: 1 }}
            >
              {SMOKING_HABITS.map((s) => (
                <ToggleButton key={s} value={s}>
                  {s}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            {errors.smokingHabits && <FormHelperText error>{errors.smokingHabits}</FormHelperText>}
          </FormControl>

          <SwitchField
            label="Exclude rejectors"
            description="Removes panelists who reject the category outright. Adds “Sans Rejectors” to the target group name."
            checked={state.sansRejector}
            onChange={(checked) => dispatch(setField('sansRejector', checked))}
          />

          <ChipSelect
            label="Ethnicities"
            options={ETHNICITIES.map((e) => ({
              value: e.key,
              label: e.label,
              disabled: !isUsa,
            }))}
            selected={ETHNICITIES.filter((e) => state.ethnicities[e.key]).map((e) => e.key)}
            onToggle={(key) =>
              dispatch(
                setField('ethnicities', {
                  ...state.ethnicities,
                  [key]: !state.ethnicities[key as keyof typeof state.ethnicities],
                }),
              )
            }
            helperText={
              isUsa
                ? 'Panelists of the selected ethnicities are included.'
                : 'Ethnicity filtering is only available for the United States.'
            }
          />
        </Box>
      </FormSection>
    </>
  );
};

export default DemographicsStep;
